import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@ascleo/ui/components/ui/dialog";
import { Button } from "@ascleo/ui/components/ui/button";
import { Delete, Eraser, KeyRound, Loader2, X } from "lucide-react";
import { cn } from "@ascleo/ui/lib/utils";
import { useTranslation } from "react-i18next";
import { frontdeskDiagnosticLogger } from "../lib/diagnostic-logger";
import { reportOperationalError } from "../lib/operational-error-telemetry";

interface PinVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVerified: () => void;
  /** Async function to verify the PIN - returns true if correct */
  verifyPin: (pin: string) => Promise<boolean>;
}

/**
 * Staff PIN prompt in front of the kiosk settings: an on-screen keypad (no
 * system keyboard on the kiosk), 4–8 digits, verified through `verifyPin`.
 * Closes itself and calls `onVerified` on success; a wrong PIN clears the
 * entry and says so. The keypad keys stay active while the entry is empty
 * (pressing them then does nothing) so they never look disabled.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <PinVerificationDialog
 *   open={isPinOpen}
 *   onOpenChange={setIsPinOpen}
 *   onVerified={openSettings}
 *   verifyPin={(pin) => platform.verifyPin(pin)}
 * />
 * ```
 */
export function PinVerificationDialog({
  open,
  onOpenChange,
  onVerified,
  verifyPin,
}: PinVerificationDialogProps) {
  const { t } = useTranslation();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const lastOpenRef = useRef(open);

  if (open !== lastOpenRef.current) {
    lastOpenRef.current = open;
    if (open) {
      setPin("");
      setError(null);
      setIsVerifying(false);
    }
  }

  const handleNumberClick = (digit: string) => {
    if (pin.length < 8 && !isVerifying) {
      const newPin = pin + digit;
      setPin(newPin);
      setError(null);
    }
  };

  const handleBackspace = () => {
    if (!isVerifying) {
      setPin(pin.slice(0, -1));
      setError(null);
    }
  };

  const handleClear = () => {
    if (!isVerifying) {
      setPin("");
      setError(null);
    }
  };

  const handleVerify = async (pinToVerify: string = pin) => {
    if (pinToVerify.length < 4) return;

    setIsVerifying(true);
    setError(null);

    try {
      const isCorrect = await verifyPin(pinToVerify);
      if (isCorrect) {
        setIsVerifying(false);
        onVerified();
        onOpenChange(false);
        setPin("");
      } else {
        setIsVerifying(false);
        setError(t("settings.pin.error"));
        setPin("");
      }
    } catch (err) {
      frontdeskDiagnosticLogger.error("[PIN] Verification failed", err);
      void reportOperationalError({
        action: "settings.pin-verification",
        severity: "error",
        source: "native",
        component: "PinVerificationDialog",
        error: err,
      });
      setIsVerifying(false);
      setError(t("settings.pin.error"));
      setPin("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="frontdesk-pin-verification-dialog"
        showCloseButton={false}
        className="max-h-[calc(100dvh-2rem)] overflow-y-auto p-6 sm:max-w-md"
      >
        <div className="flex flex-col items-center gap-6">
          {/* Icon */}
          <div className="flex size-16 items-center justify-center rounded-full bg-primary-subtle">
            <KeyRound aria-hidden className="size-8 text-primary-ink" />
          </div>

          {/* Title & Description */}
          <div className="flex flex-col gap-2 text-center">
            <DialogTitle className="font-heading text-2xl font-normal tracking-[-0.01em]">
              {t("settings.pin.title")}
            </DialogTitle>
            <DialogDescription className="text-lg">
              {t("settings.pin.description")}
            </DialogDescription>
          </div>

          {/* PIN Display */}
          <div className="w-full space-y-2">
            <output
              className={cn(
                "flex min-h-[80px] items-center justify-center gap-3 rounded-2xl border-2 bg-foreground-soft p-6",
                error
                  ? "border-destructive motion-safe:animate-[kioskShake_360ms]"
                  : "border-border",
              )}
              aria-live="polite"
            >
              {Array.from({ length: 8 }).map((_, index) => (
                <span
                  key={index}
                  className={cn(
                    "size-3 rounded-full transition-all duration-200",
                    index < pin.length
                      ? error
                        ? "bg-destructive scale-110"
                        : "bg-primary scale-110"
                      : "bg-muted-foreground/20",
                  )}
                />
              ))}
            </output>

            {/* Error Message */}
            {error && (
              <p
                data-testid="pin-error"
                className="text-center text-base font-semibold text-destructive-ink"
                role="alert"
              >
                {error}
              </p>
            )}
          </div>

          {/* Numpad */}
          <div className="w-full space-y-3">
            {/* Number Grid */}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <Button
                  key={num}
                  data-testid={`pin-digit-${num}`}
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => handleNumberClick(num.toString())}
                  disabled={isVerifying}
                  className="h-16 text-2xl font-semibold hover:scale-105 transition-transform active:scale-95"
                >
                  {num}
                </Button>
              ))}
            </div>

            {/* Bottom Row: Clear, 0, Backspace */}
            <div className="grid grid-cols-3 gap-3">
              <Button
                data-testid="pin-clear"
                type="button"
                variant="outline"
                size="lg"
                onClick={handleClear}
                disabled={isVerifying}
                className="h-16 gap-2 text-lg font-medium"
              >
                <Eraser aria-hidden className="size-5" />
                {t("common.clear")}
              </Button>
              <Button
                data-testid="pin-digit-0"
                type="button"
                variant="outline"
                size="lg"
                onClick={() => handleNumberClick("0")}
                disabled={isVerifying}
                className="h-16 text-2xl font-semibold hover:scale-105 transition-transform active:scale-95"
              >
                0
              </Button>
              <Button
                data-testid="pin-backspace"
                type="button"
                variant="outline"
                size="lg"
                onClick={handleBackspace}
                disabled={isVerifying}
                className="h-16"
                aria-label={t("settings.pin.backspace")}
                title={t("settings.pin.backspace")}
              >
                <Delete aria-hidden className="size-6" />
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex w-full gap-3">
            <Button
              data-testid="pin-cancel"
              type="button"
              variant="outline"
              size="lg"
              onClick={() => onOpenChange(false)}
              className="h-16 flex-1 gap-2 text-lg"
              disabled={isVerifying}
            >
              <X aria-hidden className="size-5" />
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="pin-confirm"
              type="button"
              size="lg"
              onClick={() => handleVerify()}
              className="h-16 flex-1 gap-2 text-lg"
              disabled={pin.length < 4 || isVerifying}
              aria-busy={isVerifying}
            >
              {isVerifying ? (
                <Loader2 aria-hidden className="size-5 animate-spin" />
              ) : (
                <KeyRound aria-hidden className="size-5" />
              )}
              {isVerifying ? t("common.verifying") : t("common.confirm")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
